import { Controller } from '@hotwired/stimulus';

const HEIC_TYPES = /heic|heif/i;
const HEIC_EXT = /\.heic$/i;
const HEIC_BRANDS = /^(heic|heix|hevc|hevx|mif1|msf1)$/i;
// Full-size phone photos (10MB+) fail on weak mobile networks. Server-side backstop:
// ImageConverter (app/lib/image_converter.rb) with the same MAX_DIMENSION.
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.8;

export default class extends Controller {
  static targets = ['input', 'preview'];

  connect() {
    console.log('dynform/photo_controller connected');
    this.previewObjectUrls = [];
  }

  disconnect() {
    this.revokePreviewUrls();
  }

  reset() {
    this.revokePreviewUrls();
    this.inputTarget.value = null;
    this.previewTarget.innerHTML = '';
  }

  photoViaBrowse() {
    this.inputTarget.removeAttribute('capture');
  }

  photoViaCamera() {
    this.inputTarget.setAttribute('capture', 'camera');
  }

  async handleImageSelect(event) {
    const files = event.target.files;

    if (files.length < 1) {
      return;
    }

    if (!(await this.isImageFile(files[0]))) {
      return;
    }

    // Prefilled stored photos (decrypt_controller.js) must stay byte-identical: re-encoding would
    // lose quality on every edit and change the blob's _p hash although the photo is unchanged.
    const file = event.prefill ? files[0] : await this.downscale(files[0]);

    this.previewTarget.innerHTML = '';
    this.revokePreviewUrls();

    // Create a container for each image and its remove button
    const imageContainer = document.createElement('div');
    imageContainer.classList.add(
      'image-preview-container',
      'flex',
      'flex-col',
      'items-center',
      'mr-2',
      'mb-2'
    );

    const img = document.createElement('img');
    img.classList.add('w-24', 'h-24', 'object-cover', 'mt-2'); // Tailwind classes
    img.file = file;
    imageContainer.appendChild(img);

    try {
      img.src = await this.previewUrlFor(file);
    } catch (error) {
      console.error('Failed to preview image', error);
      img.replaceWith(this.selectedFileLabel(file));
    }

    // Add a remove button
    const removeButton = document.createElement('button');
    removeButton.innerText = 'Remove';
    removeButton.classList.add('bg-pink', 'text-white', 'p-1', 'rounded', 'text-xs', 'mt-2');
    if (new URLSearchParams(document.location.search).get('view')) {
      removeButton.classList.add('hidden');
    }
    removeButton.addEventListener('click', () => {
      this.reset();
    });
    imageContainer.appendChild(removeButton);

    this.previewTarget.appendChild(imageContainer);
  }

  async previewUrlFor(file) {
    if (!(await this.isHeic(file))) {
      return this.readAsDataUrl(file);
    }

    const url = URL.createObjectURL(await this.heicToJpeg(file));
    this.previewObjectUrls.push(url);
    return url;
  }

  // Swaps the input's file for the downscaled JPEG, so the submit (summary_overlay_controller.js)
  // picks it up from FormData unchanged. Keeps the original if the browser can't decode it.
  async downscale(file) {
    try {
      const source = (await this.isHeic(file)) ? await this.heicToJpeg(file) : file;
      const bitmap = await createImageBitmap(source);
      const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);

      const ctx = canvas.getContext('2d');
      // JPEG has no alpha; without a fill, transparent areas turn black.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))),
          'image/jpeg',
          JPEG_QUALITY
        );
      });
      const resized = new File([blob], `${file.name.replace(/\.[^.]*$/, '')}.jpg`, {
        type: 'image/jpeg',
      });

      const transfer = new DataTransfer();
      transfer.items.add(resized);
      this.inputTarget.files = transfer.files;
      return resized;
    } catch (error) {
      console.warn('Could not downscale photo, uploading original', error);
      return file;
    }
  }

  async heicToJpeg(file) {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
    return Array.isArray(converted) ? converted[0] : converted;
  }

  async isImageFile(file) {
    if (file.type.startsWith('image/')) {
      return true;
    }

    if (HEIC_EXT.test(file.name)) {
      return true;
    }

    return this.isHeic(file);
  }

  async isHeic(file) {
    if (HEIC_TYPES.test(file.type)) {
      return true;
    }

    if (HEIC_EXT.test(file.name)) {
      return true;
    }

    const header = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(header);
    if (bytes.length < 12) {
      return false;
    }

    const ftyp = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
    if (ftyp !== 'ftyp') {
      return false;
    }

    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    return HEIC_BRANDS.test(brand);
  }

  readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  selectedFileLabel(file) {
    const label = document.createElement('p');
    label.classList.add('text-sm', 'text-gray-600', 'mt-2', 'text-center');
    label.textContent = `Photo selected (${file.name})`;
    return label;
  }

  revokePreviewUrls() {
    for (const url of this.previewObjectUrls) {
      URL.revokeObjectURL(url);
    }
    this.previewObjectUrls = [];
  }
}
